import { useEffect, useLayoutEffect, useState } from "react";
import Modal from "./Modal";

// Opens a panel once, invisibly, shortly after the app starts, then closes it.
//
// Why: the first time an add panel opened after a reload it showed up ~4x
// later than every later time (measured ~400ms vs ~100ms at 6x CPU throttle),
// almost all of it the browser's first layout of that panel inside the Modal.
// Laying the bare form out elsewhere didn't help; going through the real Modal
// does. So this renders the exact same Modal + panel with `warm` (opacity 0,
// inert, behind everything) for a couple of frames, in idle time, once per `id`
// per app load (module-level, so remounting tabs don't repeat it).
const warmed = new Set();

// Browsers download each web-font weight only when some text first uses it, so
// the panels' own weights (400-700 Quicksand) were arriving mid-open and forcing
// a second layout. Ask for all of them up front, once.
let fontsPromise = null;
function fontsLoaded() {
  if (!fontsPromise) {
    const f = typeof document !== "undefined" && document.fonts;
    fontsPromise = !f ? Promise.resolve() : Promise.all(
      [400, 500, 600, 700].map((w) => f.load(`${w} 16px Quicksand`).catch(() => null))
    ).then(() => f.ready).catch(() => null);
  }
  return fontsPromise;
}

export function PanelWarmup({ id, delay = 600, children }) {
  const [phase, setPhase] = useState(() => (warmed.has(id) ? "done" : "wait"));

  useEffect(() => {
    if (phase !== "wait") return;
    // Wait for the web fonts too: a warm-up laid out before they finish is
    // thrown away when they arrive ("Fonts changed" relayout), and the first
    // real open is slow again.
    let idle = null, cancelled = false;
    const t = setTimeout(() => {
      fontsLoaded().then(() => {
        if (cancelled) return;
        const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
        idle = ric(() => { if (!cancelled) setPhase("on"); }, { timeout: 2000 });
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(t);
      if (idle != null && window.cancelIdleCallback) window.cancelIdleCallback(idle);
    };
  }, [phase, delay]);

  useLayoutEffect(() => {
    if (phase !== "on") return;
    warmed.add(id);
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setPhase("done")));
    return () => cancelAnimationFrame(r);
  }, [phase, id]);

  if (phase !== "on") return null;
  return <Modal warm onClose={() => {}} blur={false} dim={0}>{children}</Modal>;
}
